import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StarIcon, SpinnerGapIcon, PlusIcon } from "@phosphor-icons/react";
import { fetchWatchlist } from "../lib/api";
import { useWatchlist } from "../context/WatchlistContext";
import { isValidSolanaAddress } from "../lib/solana";
import { CoinTable } from "../components/CoinTable";
import { useState } from "react";

export default function Watchlist() {
  const { watchlist, toggle, isWatched } = useWatchlist();
  const navigate = useNavigate();
  const [sort, setSort] = useState("score");
  const [order, setOrder] = useState("desc");
  const [ca, setCa] = useState("");

  const onAddCa = () => {
    const addr = ca.trim();
    if (!isValidSolanaAddress(addr)) { toast.error("Enter a valid Solana contract address."); return; }
    if (isWatched(addr)) { toast.error("That coin is already on your watchlist."); return; }
    toggle(addr);
    setCa("");
    toast.success("Coin added to your watchlist.");
  };

  const { data, isLoading } = useQuery({
    queryKey: ["watchlist", watchlist],
    queryFn: () => fetchWatchlist(watchlist),
    enabled: watchlist.length > 0,
    refetchInterval: 60000,
  });

  const onSort = (key) => {
    if (sort === key) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else { setSort(key); setOrder("desc"); }
  };

  const keyfn = (c) => {
    if (sort === "volume") return c.volume?.h24 || 0;
    if (sort === "marketCap") return c.marketCap || 0;
    if (sort === "liquidity") return c.liquidity || 0;
    if (sort === "age") return c.ageHours || 0;
    if (sort === "change") return c.priceChange?.h24 || 0;
    return c.score.total;
  };
  const coins = [...(data?.coins || [])].sort((a, b) => (keyfn(b) - keyfn(a)) * (order === "desc" ? 1 : -1));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">WATCHLIST</h1>
        <p className="text-muted-foreground mt-2 text-sm">Coins you're tracking. Synced to your account across all your devices.</p>
      </div>

      {/* Add CA bar */}
      <div className="border border-white/10 bg-card p-4 flex flex-col sm:flex-row gap-3">
        <input
          data-testid="watchlist-ca-input"
          value={ca}
          onChange={(e) => setCa(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAddCa()}
          placeholder="Add a coin by contract address (CA) — even if it's not on the Radar…"
          className="flex-1 bg-black/40 border border-white/10 px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-neon/50 transition-colors"
        />
        <button
          data-testid="watchlist-add-ca-btn"
          onClick={onAddCa}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors"
        >
          <PlusIcon size={16} weight="bold" /> Add to watchlist
        </button>
      </div>

      {watchlist.length === 0 ? (
        <div className="border border-white/10 bg-card py-24 flex flex-col items-center gap-4 text-center">
          <StarIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm">Your watchlist is empty.</p>
          <button data-testid="go-radar-btn" onClick={() => navigate("/")} className="px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors">
            Browse the Radar
          </button>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Loading your coins…</span>
        </div>
      ) : (
        <CoinTable coins={coins} sort={sort} order={order} onSort={onSort} />
      )}
    </div>
  );
}
